import { Menu, Transition } from '@headlessui/react';
import type { ComponentProps } from 'react';
import { Fragment, useCallback } from 'react';

import useLabels from '~hooks/useLabels';
import { SortOrder } from '~modules/media-library/model/SortOrder';
import { SortButton } from '~modules/media-library/view/SortDropdown/SortButton';
import { SortMenuPanel } from '~modules/media-library/view/SortDropdown/SortMenuPanel';

type SortDropdownProps = Omit<ComponentProps<typeof SortMenuPanel>, 'onToggleSortOrder' | 'onClickOption'> & {
  onChange: (sortKey: string, sortOrder: SortOrder) => void;
};

export function SortDropdown({ onChange, ...SortMenuPanelProps }: SortDropdownProps) {
  // labels
  const { labelMediaLibrarySortBy, labelMediaLibrarySortByDefault } = useLabels([
    'labelMediaLibrarySortBy',
    'labelMediaLibrarySortByDefault',
  ]);

  // Handler: click on sort option
  const handleOnClickOption = useCallback(
    (sortKey: string, sortOrder?: SortOrder) => {
      onChange?.(sortKey, sortOrder != null ? sortOrder : SortMenuPanelProps.sortOrder);
    },
    [SortMenuPanelProps.sortOrder, onChange],
  );

  // Handler: click on sort order
  const handleOnToggleSortOrder = useCallback(() => {
    const nextSortOrder = SortMenuPanelProps.sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
    onChange?.(SortMenuPanelProps.sortKey, nextSortOrder);
  }, [SortMenuPanelProps.sortKey, SortMenuPanelProps.sortOrder, onChange]);

  // derived values
  const sortOption = SortMenuPanelProps.options.get(SortMenuPanelProps.sortKey);
  const labelSortedBy = sortOption?.label || labelMediaLibrarySortByDefault;

  return (
    <Menu as="div" className="ml--sort-dropdown relative inline-block">
      <div className="flex items-center">
        <div className="select-none">{labelMediaLibrarySortBy}</div>
        <Menu.Button as={SortButton}>{labelSortedBy}</Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 min-w-52 origin-top-right outline-none z-30">
          <SortMenuPanel
            {...SortMenuPanelProps}
            onClickOption={handleOnClickOption}
            onToggleSortOrder={handleOnToggleSortOrder}
          />
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
